import { LISTING_CALLER_SOURCE } from "@tm/api-utils";
import { getAnalytics } from "@tm/insurance/analytics";
import {
  getSelectedCountriesFromLocalStorage,
  initializePageTracking,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import { IListingClientSearchIntent } from "@tm/insurance/api";
import {
  navigationActions,
  pageStack,
  PAGE_NAME_CONSTANTS,
} from "@tm/insurance/navigation";
import {
  addDaysToDate,
  convertDateToUnix,
  convertUnixToDate,
} from "@tm/insurance/utils";
import { usePlanType } from "@tm/ui";
import { storePageTransitionStart } from "@tm/utility/analytics";
import { theme } from "@tm/utility/global";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Spinner from "../../../components/Spinner/Spinner";
import WebToast from "../../../components/Toast/WebToast";
import { ListingRevampProps } from "../types";
import { AMT_ATTACH_ID, CONTEXT_EDIT_ID } from "./componentMapper";
import ComponentRenderer from "./ComponentRenderer";
import { IAmtAttachConfig } from "./components/AmtAttach/types";
import ComparePlans from "./components/ComparePlans";
import { IComparePlansData } from "./components/ComparePlans/types";
import ContextEdit from "./components/ContextEdit";
import {
  ContentWrapper,
  ListingRevampLayout,
  LoadingOverlay,
} from "./InsuranceListingRevamp.styles";
import {
  clearAmtSession,
  getAmtOriginalParams,
  getLandingSource,
  setAmtOriginalParams,
  setLandingSource,
} from "./sessionStore";
import { trackListingClick } from "./trackingUtils";
import { useListingData } from "./useListingData";
import { buildTravellerDetailsParams } from "./utils";

const FULL_COVERAGE_CODES = new Set(["USA", "CAN", "WW"]);
const WW_CODE = "WW";
const WWEUC_CODE = "WWEUC";
const DEFAULT_AMT_TRIP_OPTION = "MULTITRIP";
const DEFAULT_AMT_DURATION_DAYS = 365;

const analytics = getAnalytics();

interface InsuranceListingRevampProps extends ListingRevampProps {
  onLoginRequest?: () => void;
}

const InsuranceListingRevamp = ({
  listingData: initialData,
  onLoginRequest,
}: InsuranceListingRevampProps) => {
  const { listingData, isLoading, error, clearError, refetch } =
    useListingData(initialData);
  const originalParamsRef = useRef<IListingClientSearchIntent | null>(null);
  const [showComparePlans, setShowComparePlans] = useState(false);
  const { type } = usePlanType();
  const hasTrackedPageLoad = useRef(false);
  const [pageTracked, setPageTracked] = useState(false);

  const isDirectLanding = useMemo(() => {
    if (pageStack.peek() === PAGE_NAME_CONSTANTS.COMMON_LANDING) {
      setLandingSource("COMMON_LANDING");
      return false;
    }
    if (getLandingSource() === "COMMON_LANDING") return false;
    setLandingSource("DIRECT");
    return true;
  }, []);

  const getTripOption = () =>
    new URLSearchParams(window.location.search).get("tripOption") ?? undefined;
  const getPlanType = () =>
    new URLSearchParams(window.location.search).get("planType") ?? undefined;

  const [tripOption, setTripOption] = useState<string | undefined>(undefined);
  const [planTypeState, setPlanTypeState] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    setTripOption(getTripOption());
    setPlanTypeState(getPlanType());
  }, [listingData]);

  // ──── Page Load Tracking ────
  useEffect(() => {
    if (hasTrackedPageLoad.current || !listingData) return;
    hasTrackedPageLoad.current = true;

    try {
      pageStack.push(PAGE_NAME_CONSTANTS.LISTING);

      const queryParams = new URLSearchParams(window.location.search);
      const countries = queryParams.get("countries") || "";
      const startDate = queryParams.get("startDate") || "";
      const endDate = queryParams.get("endDate") || "";
      const travellers = queryParams.get("travellers") || "1";
      const segment =
        queryParams.get("segment") || queryParams.get("tripOption") || "";
      const subpageName = queryParams.get("type");
      const experiment = listingData?.pageData?.experiment || "";

      const startDateObj = startDate
        ? convertUnixToDate(Number(startDate))
        : undefined;
      const endDateObj = endDate
        ? convertUnixToDate(Number(endDate))
        : undefined;

      analytics?.trackPageLoadEvent(
        {
          pageName: "LISTING",
          v2: true,
          country: countries,
          startDate:
            startDateObj?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") ||
            "",
          endDate:
            endDateObj?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") || "",
          travellers: `${Number(travellers)}|0|0`,
          segment,
          experiment,
        },
        type
      );
      setPageTracked(true);

      const selectedCountries = countries
        ? getSelectedCountriesFromLocalStorage(countries.split(","))
        : [];

      initializePageTracking(
        {
          page_context: {
            page_name: "listing",
            page_type: "funnel",
            funnel_step: "listing",
            ...(subpageName && { subpage_name: subpageName }),
          },
        },
        {
          countries: selectedCountries,
          journeyType: segment,
          travellers: parseInt(travellers) || 1,
          fromDateTime: startDateObj ? convertDateToUnix(startDateObj) : 0,
          toDateTime: endDateObj ? convertDateToUnix(endDateObj) : 0,
        }
      );
    } catch (err) {
      console.error("Listing page load tracking failed", err);
    }

    return () => {
      try {
        triggerPageExit();
      } catch (err) {
        console.error("Listing page exit tracking failed", err);
      }
    };
  }, [listingData, type]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(clearError, 4000);
    return () => clearTimeout(timer);
  }, [error, clearError]);

  const comparePlansData = listingData?.pageData?.comparePlansSection as
    | IComparePlansData
    | undefined;

  const { contextEditData, contentComponents, amtConfig } = useMemo(() => {
    const components = listingData?.components ?? [];
    const ctxEdit = components.find((c) => c.id === CONTEXT_EDIT_ID);
    const rest = components.filter((c) => c.id !== CONTEXT_EDIT_ID);
    const amt = components.find((c) => c.id === AMT_ATTACH_ID);
    return {
      contextEditData: ctxEdit?.data,
      contentComponents: rest,
      amtConfig: amt?.data?.config as IAmtAttachConfig | undefined,
    };
  }, [listingData]);

  const amtEnabled = tripOption === DEFAULT_AMT_TRIP_OPTION;

  const onAmtToggle = useCallback(
    (enabled: boolean) => {
      const dates = contextEditData?.travelDetails?.dates;
      const countries = contextEditData?.travelDetails?.countries;
      const travellers = contextEditData?.travelDetails?.travellerCount;

      if (!dates?.startDate || !countries?.countryCodes) return;

      const actionType = enabled ? "AMT_toggle_enabled" : "AMT_toggle_disabled";
      trackListingClick(actionType, type);

      if (enabled) {
        const currentUrl = new URL(window.location.href);
        originalParamsRef.current = {
          country: countries.countryCodes,
          departureDateTime: dates.startDate,
          arrivalDateTime: dates.endDate ?? dates.startDate,
          numberOfTravellers: travellers?.value ?? 1,
          tripOption: currentUrl.searchParams.get("tripOption") ?? undefined,
          planType: currentUrl.searchParams.get("planType") ?? undefined,
        };

        const needsFullCoverage = countries.countryCodes.some((c: string) =>
          FULL_COVERAGE_CODES.has(c)
        );
        const multitripCountry = needsFullCoverage ? WW_CODE : WWEUC_CODE;

        const startDate = convertUnixToDate(dates.startDate);
        if (!startDate) return;
        const durationAllowed =
          amtConfig?.durationAllowed ?? DEFAULT_AMT_DURATION_DAYS;
        const newEndDate = addDaysToDate(startDate, durationAllowed);
        if (!newEndDate) return;
        const newEndTimestamp = convertDateToUnix(newEndDate);

        refetch({
          country: [multitripCountry],
          departureDateTime: dates.startDate,
          arrivalDateTime: newEndTimestamp,
          numberOfTravellers: travellers?.value ?? 1,
          tripOption: amtConfig?.tripOption ?? DEFAULT_AMT_TRIP_OPTION,
          planType: undefined,
          callerSource: LISTING_CALLER_SOURCE,
        }).then(() => {
          if (originalParamsRef.current) {
            setAmtOriginalParams(originalParamsRef.current);
          }
        });
      } else {
        const restoreParams =
          originalParamsRef.current ?? getAmtOriginalParams();
        if (restoreParams) {
          refetch(restoreParams).then(() => {
            originalParamsRef.current = null;
            clearAmtSession();
          });
        }
      }
    },
    [contextEditData, amtConfig, refetch, type]
  );

  const handleCompareClick = useCallback(() => {
    if (comparePlansData) {
      setShowComparePlans(true);
      trackListingClick("compare_clicked", type);
    }
  }, [comparePlansData, type]);

  const handleCompareClose = useCallback(() => {
    setShowComparePlans(false);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showComparePlans ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showComparePlans]);

  const handleCompareBuyNow = useCallback(
    (params?: { quoteId?: string; productId?: number }) => {
      if (!params?.quoteId) return;

      const travellerParams = buildTravellerDetailsParams(
        params.productId ?? "",
        params.quoteId
      );

      trackListingClick("buy_from_compare", type, {
        components: [
          {
            product_list: [
              { id: params.productId != null ? String(params.productId) : "" },
            ],
          },
        ],
      });
      triggerPageExit();
      storePageTransitionStart("listing");
      navigationActions.navigateToTravellerDetails(travellerParams);
    },
    [type]
  );

  return (
    <ListingRevampLayout id="listing-revamp">
      {contextEditData && (
        <ContextEdit
          {...contextEditData}
          onRefetch={refetch}
          tripOption={tripOption}
          planType={planTypeState}
          isDirectLanding={isDirectLanding}
          pageTracked={pageTracked}
        />
      )}
      <ContentWrapper>
        {showComparePlans && comparePlansData ? (
          <ComparePlans
            data={comparePlansData}
            onClose={handleCompareClose}
            onBuyNow={handleCompareBuyNow}
          />
        ) : (
          <ComponentRenderer
            components={contentComponents}
            onLoginRequest={onLoginRequest}
            onAmtToggle={onAmtToggle}
            amtEnabled={amtEnabled}
            onCompareClick={handleCompareClick}
            pageTracked={pageTracked}
          />
        )}
      </ContentWrapper>
      {isLoading && (
        <LoadingOverlay>
          <Spinner spinnerWidth={40} thickness={3} />
        </LoadingOverlay>
      )}
      {error && (
        <WebToast
          type="error"
          toastTxt={error}
          position="fixed"
          bottom="24px"
          right="24px"
          width="360px"
          color={theme.greyText}
        />
      )}
    </ListingRevampLayout>
  );
};

export default InsuranceListingRevamp;
